import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute } from "@tanstack/react-router";
import { HelpfulPricingGuides } from "@/components/HelpfulPricingGuides";
import { Link } from "@tanstack/react-router";
import { Phone, CheckCircle, ArrowRight, AlertTriangle, Search, Lightbulb } from "lucide-react";
import { services, getBusinessConfig } from "@/lib/site-config";
import { faqSchema, FAQAccordion } from "@/components/FAQAccordion";
import { HelpfulGuidesGrid } from "@/components/HelpfulGuidesGrid";
import { ServiceHero } from "@/components/ServiceHero";
import { ReassuranceBanner } from "@/components/ReassuranceBanner";
import { ProcessSteps } from "@/components/ProcessSteps";
import { CTABlock } from "@/components/CTABlock";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { seoHead, serviceSchema, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { getPublicConfig } from "@/lib/site-content.functions";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { InternalLinksBlock } from "@/components/InternalLinksBlock";
import { DriverSupportCTA } from "@/components/DriverSupportCTA";
import { WhyChooseWoodseer } from "@/components/WhyChooseWoodseer";

const svc = services[2]; // diagnostics

const extendedFaqs = [
  ...svc.faqs,
  { q: "Why do some garages just clear the code and charge me?", a: "Clearing a code without investigating the root cause is a shortcut. The light comes back, you pay again. We dig deeper to find the actual fault so the fix lasts." },
  { q: "Can you diagnose intermittent faults?", a: "Yes. Intermittent issues are harder to catch but we use live data monitoring and road testing to reproduce and identify them. It may take longer, but we find the cause." },
  { q: "Do I need diagnostics if my car just feels 'off'?", a: "Yes — reduced power, rough idling, poor fuel economy, or a general feeling that something isn't right are all worth investigating. Our diagnostics can often reveal issues before they become serious." },
  { q: "How much does a diagnostic scan cost?", a: "Standard OBD-II diagnostic scan is £60. Complex multi-system or intermittent-fault investigations are quoted up front. If you go ahead with the repair we recommend, the diagnostic fee is refunded against the bill." },
  { q: "How long does a diagnostic take?", a: "A basic scan and fault-code read takes 30–45 minutes. Deeper investigations (live data, wiring checks, road tests) can take 1–3 hours. We'll give you an honest time estimate before we start." },
  { q: "What's included in the diagnostic?", a: "OBD-II scan of every module (engine, ABS, airbag, transmission, body), live sensor data review, fault-code interpretation in plain English, a written report of findings, and a clear repair quote — never just a code and a guess." },
];


export const Route = createFileRoute("/diagnostics")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) => seoHead({
    path: "/diagnostics",
    title: "Car Diagnostics East London E14 | Warning Lights | Woodseer Autos",
    description: `Engine diagnostics and warning-light investigation in East London. We find the cause, not just the code. Clear quote before any work. Call ${NAP.phone}.`,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Diagnostics", path: "/diagnostics" },
    ],
    jsonLd: [
      localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
      faqSchema(extendedFaqs),
      serviceSchema({
        name: "Vehicle Diagnostics — Woodseer Autos",
        serviceType: "Vehicle Diagnostics",
        description: "Professional OBD-II engine diagnostics, fault code reading and warning light investigation in East London.",
        offers: { price: "60.00", priceCurrency: "GBP", description: "Diagnostic scan from £60; refunded against any repair we carry out." },
      }),
    ],
  }),
  component: DiagnosticsPage,
});

function DiagnosticsPage() {
  const biz = getBusinessConfig();

  return (
    <>
      <ServiceHero
        headline={svc.headline}
        intro={svc.intro}
        image="/images/hero-diagnostics.jpg"
        imageAlt="Diagnostic equipment at Woodseer Autos, East London"
      />

      <ReassuranceBanner />

      {/* Root Cause Approach */}
      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">We Find the Cause, Not Just the Code</h2>
            <p className="mt-4 text-center text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Many garages plug in a reader, see a fault code, and recommend replacing the part the code points to. That's not diagnostics — that's guessing. We investigate the actual root cause so the fix lasts and you don't pay for unnecessary parts.
            </p>
            <StaggerContainer className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: Search, title: "Read & Analyse", desc: "We read fault codes and live data from your vehicle's electronic systems — not just a quick scan." },
                { icon: Lightbulb, title: "Investigate the Cause", desc: "Codes tell us where to look, not what's broken. We test, inspect and verify before recommending any work." },
                { icon: CheckCircle, title: "Fix It Properly", desc: "Once we've identified the root cause, we fix it right first time. No guesswork, no trial-and-error at your expense." },
              ].map((step) => (
                <StaggerItem key={step.title}>
                  <div className="flex flex-col items-center text-center p-6 rounded-xl border border-border bg-background/50">
                    <step.icon className="h-8 w-8 text-primary" />
                    <h3 className="mt-3 font-semibold text-foreground">{step.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* Warning Lights */}
      <AnimatedSection>
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">Common Warning Lights We Investigate</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {svc.commonIssues.map((issue) => (
                <StaggerItem key={issue}>
                  <div className="flex items-start gap-3 rounded-xl border border-border p-4 text-sm text-muted-foreground bg-card/50">
                    <AlertTriangle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {issue}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* What's included */}
      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">What's Included</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
              {svc.included.map((item) => (
                <StaggerItem key={item}>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {item}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* Signs */}
      <AnimatedSection>
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">When You Should Get a Diagnostic Check</h2>
            <ul className="mt-8 space-y-3 max-w-xl mx-auto">
              {svc.signs.map((sign) => (
                <li key={sign} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <span className="text-primary font-bold">→</span>
                  {sign}
                </li>
              ))}
            </ul>
          </div>
        </section>
      </AnimatedSection>

      {/* Why choose us */}
      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">Why Choose Woodseer Autos for Diagnostics</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {svc.whyUs.map((reason) => (
                <StaggerItem key={reason}>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {reason}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      <AnimatedSection>
        <ProcessSteps steps={svc.process} title="How Diagnostics Works" />
      </AnimatedSection>

      <AnimatedSection>
      <HelpfulGuidesGrid slugs={["engine-light-east-london","mot-warning-lights-before-test","car-pulling-to-one-side"]} title="Helpful guides for this service" compact />

        <FAQAccordion faqs={extendedFaqs} />
      </AnimatedSection>

      <AnimatedSection>
        <ServiceTestimonials
          service="Diagnostics"
          title="What diagnostics customers say"
          itemReviewedType="Service"
          itemReviewedName="Engine Diagnostics — Woodseer Autos"
          serviceType="Vehicle Diagnostics"
        />
      </AnimatedSection>

      <AnimatedSection>
        <InternalLinksBlock currentService="diagnostics" serviceForAreaLinks="diagnostics" />
      </AnimatedSection>

      <HelpfulPricingGuides currentService="diagnostics" />
      <div className="mx-auto max-w-4xl px-4"><DriverSupportCTA page="diagnostics" service="diagnostics" bookingContext="diagnostics-driver-support" headline="Warning light on a high-mileage or work vehicle?" /></div>
      <WhyChooseWoodseer page="diagnostics" />
      <CTABlock headline="Warning Light On?" subtext="Don't ignore it. Call us and we'll find out what's really going on — fast, honest and fair." />
    </>
  );
}
